<template>
  <div class="dash" :class="{ 'nav-open': mobileNavOpen }">
    <a class="skip-link" href="#dashboard-main">跳到主要内容</a>
    <button v-if="mobileNavOpen" class="nav-scrim" aria-label="关闭导航" @click="closeMobileNav"></button>

    <aside
      id="dashboard-nav"
      ref="sidebar"
      class="sidebar"
      :aria-hidden="isMobileViewport && !mobileNavOpen ? 'true' : 'false'"
      :inert="isMobileViewport && !mobileNavOpen ? '' : null"
    >
      <div class="sidebar-head">
        <router-link to="/chat" class="brand" aria-label="xyo.chat 聊天首页" @click.native="closeMobileNav">
          <span class="brand-mark" aria-hidden="true">x</span>
          <span class="meta">
            <strong>xyo.chat</strong>
            <small>赛博前任 · 陪伴空间</small>
          </span>
        </router-link>
        <button ref="navClose" class="nav-close" aria-label="关闭导航" @click="closeMobileNav">
          <i class="el-icon-close"></i>
        </button>
      </div>

      <nav ref="nav" class="nav" aria-label="后台主导航">
        <section v-for="section in visibleMenuSections" :key="section.label" class="nav-section">
          <p class="nav-label">{{ section.label }}</p>
          <router-link
            v-for="item in section.items"
            :key="item.path + item.label"
            :to="item.path"
            class="nav-item"
            :class="{ active: isActive(item) }"
            @click.native="closeMobileNav"
          >
            <i :class="item.icon"></i>
            <span>{{ item.label }}</span>
            <i class="el-icon-arrow-right nav-arrow"></i>
          </router-link>
        </section>
      </nav>

      <div class="promo">
        <span class="promo-kicker">主动陪伴</span>
        <p class="title">想让小麦主动找你聊天吗？</p>
        <p class="sub">开启主动消息，关系升温更快哦~</p>
        <el-button size="mini" class="promo-btn">去开启</el-button>
      </div>
    </aside>

    <div
      class="main"
      :aria-hidden="isMobileViewport && mobileNavOpen ? 'true' : null"
      :inert="isMobileViewport && mobileNavOpen ? '' : null"
    >
      <header class="header">
        <button
          class="mobile-nav-toggle"
          type="button"
          aria-label="打开导航"
          aria-controls="dashboard-nav"
          :aria-expanded="mobileNavOpen ? 'true' : 'false'"
          @click="openMobileNav"
        >
          <i class="el-icon-menu"></i>
        </button>

        <div class="header-left">
          <slot name="header-left">
            <div class="route-context">
              <span>{{ currentPageGroup }}</span>
              <strong>{{ currentPageTitle }}</strong>
            </div>
          </slot>
        </div>

        <div class="header-right">
          <el-popover placement="bottom-end" width="300" trigger="click" popper-class="yw-popover">
            <div class="notif">
              <div class="notif-head">
                <b>通知</b>
                <a @click="markAllRead">全部已读</a>
              </div>
              <div v-for="n in notifications" :key="n.id" class="notif-item" :class="{ unread: !n.read }">
                <i :class="n.icon"></i>
                <div>
                  <p>{{ n.text }}</p>
                  <em>{{ n.time }}</em>
                </div>
              </div>
              <p v-if="!notifications.length" class="notif-empty">暂无通知</p>
            </div>
            <button slot="reference" class="header-icon" type="button" aria-label="查看通知">
              <el-badge :value="unreadCount" :hidden="unreadCount === 0" class="bell">
                <i class="el-icon-bell"></i>
              </el-badge>
            </button>
          </el-popover>

          <el-popover placement="bottom-end" width="280" trigger="click" popper-class="yw-popover">
            <div class="wx-pop">
              <div class="wx-pop-head">
                <b>微信号（{{ onlineWxCount }}/{{ wxAccounts.length }} 在线）</b>
              </div>
              <div v-for="a in wxAccounts" :key="a.id" class="wx-pop-item">
                <span class="dot" :class="a.status"></span>
                <div>
                  <b>{{ a.nickname }}</b>
                  <em>{{ a.wechatId }}</em>
                </div>
                <small>{{ a.status === "online" ? "在线" : "离线" }}</small>
              </div>
              <router-link to="/wechat-accounts" class="wx-pop-link">管理微信号 ></router-link>
            </div>
            <button slot="reference" class="wx-tag" :class="{ off: onlineWxCount === 0 }" type="button">
              <i class="el-icon-chat-round"></i>
              <span>{{ onlineWxCount > 0 ? "微信 · " + onlineWxCount + " 在线" : "微信未绑定" }}</span>
            </button>
          </el-popover>

          <el-dropdown trigger="click" @command="onUserCommand">
            <button class="user" type="button" aria-label="打开用户菜单">
              <span class="user-avatar" aria-hidden="true">{{ userInitial }}</span>
              <span class="user-name">{{ displayName }}</span>
              <i class="el-icon-arrow-down"></i>
            </button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="profile"><i class="el-icon-user"></i> 个人中心</el-dropdown-item>
              <el-dropdown-item command="pricing"><i class="el-icon-present"></i> 套餐中心</el-dropdown-item>
              <el-dropdown-item command="logout" divided><span class="logout-text">退出登录</span></el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </header>

      <main id="dashboard-main" class="page-body" tabindex="-1" :aria-busy="showPageLoading ? 'true' : 'false'">
        <transition name="page-loading-fade">
          <page-loading v-if="showPageLoading" :text="loadingText" />
        </transition>
        <slot />
      </main>
    </div>
  </div>
</template>

<script>
import { dashboardApi, wechatApi, getUser, clearAuth } from "@/services/api";
import PageLoading from "@/components/PageLoading.vue";

const NAV_SECTIONS = [
  {
    label: "对话",
    items: [
      { path: "/chat", label: "聊天", icon: "el-icon-chat-dot-round" },
      { path: "/companions", label: "伴侣列表", icon: "el-icon-user" },
    ],
  },
  {
    label: "创作",
    items: [
      { path: "/role-cards", label: "角色卡", icon: "el-icon-postcard" },
      { path: "/companion-editor", label: "创建伴侣", icon: "el-icon-plus" },
      { path: "/image-studio", label: "图像库", icon: "el-icon-picture-outline" },
    ],
  },
  {
    label: "连接",
    items: [
      { path: "/wechat-accounts", label: "微信号管理", icon: "el-icon-chat-round" },
      { path: "/shared-conversations", label: "代聊会话", icon: "el-icon-connection" },
    ],
  },
  {
    label: "账户",
    items: [
      { path: "/pricing", label: "套餐中心", icon: "el-icon-present" },
      { path: "/profile", label: "个人中心", icon: "el-icon-user-solid" },
    ],
  },
  {
    label: "系统",
    items: [
      { path: "/admin", label: "管理后台", icon: "el-icon-setting", adminOnly: true },
      { path: "/admin/orders", label: "订单管理", icon: "el-icon-s-order", adminOnly: true },
    ],
  },
];

const ROUTE_META = {
  "/chat": { group: "对话", title: "聊天" },
  "/companions": { group: "对话", title: "伴侣列表" },
  "/companion-detail": { group: "对话", title: "伴侣详情" },
  "/role-cards": { group: "创作", title: "角色卡" },
  "/role-card-editor": { group: "创作", title: "编辑角色卡" },
  "/companion-editor": { group: "创作", title: "创建 / 编辑伴侣" },
  "/image-studio": { group: "创作", title: "图像工作台" },
  "/wechat-accounts": { group: "连接", title: "微信号管理" },
  "/shared-conversations": { group: "连接", title: "代聊会话" },
  "/pricing": { group: "账户", title: "套餐中心" },
  "/profile": { group: "账户", title: "个人中心" },
  "/admin": { group: "系统", title: "管理后台" },
  "/admin/orders": { group: "系统", title: "订单管理" },
  "/admin/prompts": { group: "系统", title: "提示词管理" },
};

export default {
  name: "DashboardLayout",
  components: { PageLoading },
  props: {
    loading: { type: Boolean, default: false },
    loadingText: { type: String, default: "正在准备页面" },
  },
  data() {
    const cached = getUser();
    return {
      menuSections: NAV_SECTIONS,
      wxAccounts: [],
      displayName: (cached && (cached.display_name || cached.email)) || "用户",
      isAdmin: cached ? cached.is_admin : false,
      notifications: [],
      mobileNavOpen: false,
      isMobileViewport: false,
      showPageLoading: false,
    };
  },
  computed: {
    visibleMenuSections() {
      return this.menuSections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => !item.adminOnly || this.isAdmin),
        }))
        .filter((section) => section.items.length);
    },
    unreadCount() {
      return this.notifications.filter((n) => !n.read).length;
    },
    onlineWxCount() {
      return this.wxAccounts.filter((a) => a.status === "online").length;
    },
    currentPageMeta() {
      return ROUTE_META[this.$route.path] || { group: "陪伴空间", title: "xyo.chat" };
    },
    currentPageGroup() {
      return this.currentPageMeta.group;
    },
    currentPageTitle() {
      return this.currentPageMeta.title;
    },
    userInitial() {
      return (this.displayName || "用").trim().slice(0, 1).toUpperCase();
    },
  },
  watch: {
    loading: {
      immediate: true,
      handler(value) {
        clearTimeout(this._pageLoadingTimer);
        if (value) {
          this._pageLoadingStartedAt = Date.now();
          this.showPageLoading = true;
          return;
        }
        if (!this.showPageLoading) return;
        const elapsed = Date.now() - (this._pageLoadingStartedAt || 0);
        const remaining = Math.max(0, 420 - elapsed);
        this._pageLoadingTimer = setTimeout(() => {
          if (!this.loading) this.showPageLoading = false;
        }, remaining);
      },
    },
    "$route.fullPath"() {
      this.closeMobileNav();
      this.scrollActiveNavIntoView();
    },
  },
  created() {
    this.loadHeader();
  },
  mounted() {
    this._mobileNavMedia = window.matchMedia("(max-width: 768px)");
    this.syncMobileViewport(this._mobileNavMedia);
    if (this._mobileNavMedia.addEventListener) {
      this._mobileNavMedia.addEventListener("change", this.syncMobileViewport);
    } else {
      this._mobileNavMedia.addListener(this.syncMobileViewport);
    }
    window.addEventListener("keydown", this.onNavigationKeydown);
    this.scrollActiveNavIntoView();
  },
  beforeDestroy() {
    clearTimeout(this._pageLoadingTimer);
    if (this._mobileNavMedia) {
      if (this._mobileNavMedia.removeEventListener) {
        this._mobileNavMedia.removeEventListener("change", this.syncMobileViewport);
      } else {
        this._mobileNavMedia.removeListener(this.syncMobileViewport);
      }
    }
    window.removeEventListener("keydown", this.onNavigationKeydown);
  },
  methods: {
    async loadHeader() {
      try {
        const dash = await dashboardApi.get();
        this.displayName = dash.user.display_name;
        this.isAdmin = dash.user.is_admin;
      } catch (e) {
        /* keep cached */
      }
      try {
        const list = await wechatApi.list();
        this.wxAccounts = (list || []).map((a) => ({
          id: a.id,
          nickname: a.nickname || a.wechat_account_id,
          wechatId: a.wechat_account_id,
          status: a.gateway_status === "online" ? "online" : "offline",
        }));
      } catch (e) {
        this.wxAccounts = [];
      }
    },
    isActive(item) {
      if (item.path === "/role-cards") return ["/role-cards", "/role-card-editor"].includes(this.$route.path);
      if (item.path === "/companions") return ["/companions", "/companion-detail"].includes(this.$route.path);
      if (item.path === "/admin") return ["/admin", "/admin/prompts"].includes(this.$route.path);
      return this.$route.path === item.path;
    },
    syncMobileViewport(event) {
      this.isMobileViewport = !!event.matches;
      if (!this.isMobileViewport) {
        this.mobileNavOpen = false;
        this._mobileNavTrigger = null;
      }
    },
    openMobileNav() {
      this._mobileNavTrigger = document.activeElement;
      this.mobileNavOpen = true;
      this.$nextTick(() => {
        if (this.$refs.navClose) this.$refs.navClose.focus();
      });
    },
    closeMobileNav() {
      if (!this.mobileNavOpen) return;
      this.mobileNavOpen = false;
      const trigger = this._mobileNavTrigger;
      this._mobileNavTrigger = null;
      this.$nextTick(() => {
        if (trigger && trigger.isConnected && typeof trigger.focus === "function") trigger.focus();
      });
    },
    scrollActiveNavIntoView() {
      this.$nextTick(() => {
        const nav = this.$refs.nav;
        if (!nav) return;
        const active = nav.querySelector(".nav-item.active");
        if (!active) return;
        const navRect = nav.getBoundingClientRect();
        const activeRect = active.getBoundingClientRect();
        if (activeRect.top < navRect.top + 4) {
          nav.scrollTop -= navRect.top + 4 - activeRect.top;
        } else if (activeRect.bottom > navRect.bottom - 4) {
          nav.scrollTop += activeRect.bottom - navRect.bottom + 4;
        }
      });
    },
    onNavigationKeydown(event) {
      if (!this.mobileNavOpen) return;
      if (event.key === "Escape") {
        event.preventDefault();
        this.closeMobileNav();
        return;
      }
      if (event.key !== "Tab" || !this.$refs.sidebar) return;
      const focusable = Array.from(
        this.$refs.sidebar.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    markAllRead() {
      this.notifications.forEach((n) => (n.read = true));
    },
    onUserCommand(cmd) {
      if (cmd === "profile") this.$router.push("/profile");
      else if (cmd === "pricing") this.$router.push("/pricing");
      else if (cmd === "logout") {
        this.$confirm("确定退出登录吗？", "退出登录", { type: "warning" })
          .then(() => {
            clearAuth();
            this.$message.success("已退出登录");
            this.$router.push("/login");
          })
          .catch(() => {});
      }
    },
  },
};
</script>

<style scoped lang="scss">
/* 2026 workspace shell — intentionally independent from the retired pink dashboard */
.dash {
  position: relative;
  display: flex;
  min-height: 100vh;
  color: var(--xyo-ink);
  background:
    radial-gradient(circle at 76% -10%, rgba(253, 99, 40, 0.1), transparent 34%),
    radial-gradient(circle at 6% 92%, rgba(114, 85, 233, 0.07), transparent 30%),
    var(--xyo-canvas);

  .skip-link {
    position: fixed;
    top: 10px;
    left: 50%;
    z-index: 2000;
    padding: 10px 16px;
    border-radius: 10px;
    color: #fff;
    background: var(--xyo-primary);
    transform: translate(-50%, -160%);
    transition: transform 180ms ease;

    &:focus {
      transform: translate(-50%, 0);
    }
  }

  .nav-scrim {
    display: none;
  }

  .sidebar {
    position: sticky;
    top: 0;
    z-index: 100;
    display: flex;
    flex: 0 0 252px;
    flex-direction: column;
    width: 252px;
    height: 100vh;
    padding: 18px 14px 16px;
    overflow-x: hidden;
    overflow-y: auto;
    border: 0;
    color: var(--xyo-sidebar-text);
    background: var(--xyo-sidebar);
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.18) transparent;

    .sidebar-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin: 0 0 18px;
      padding: 0 6px;
    }

    .brand {
      display: flex;
      align-items: center;
      min-width: 0;
      margin: 0;
      padding: 0;
      color: var(--xyo-sidebar-text);
      text-decoration: none;

      .brand-mark {
        display: grid;
        flex: 0 0 38px;
        width: 38px;
        height: 38px;
        margin-right: 11px;
        place-items: center;
        border-radius: 12px 12px 12px 4px;
        color: var(--xyo-ink-strong);
        background: var(--xyo-warm);
        font-family: Arial, sans-serif;
        font-size: 24px;
        font-weight: 900;
        letter-spacing: -0.08em;
      }

      .meta {
        display: flex;
        min-width: 0;
        flex-direction: column;

        strong {
          color: var(--xyo-sidebar-text);
          font-size: 18px;
          font-weight: 760;
          line-height: 1.2;
          letter-spacing: -0.03em;
        }

        small {
          margin-top: 3px;
          overflow: hidden;
          color: var(--xyo-sidebar-muted);
          font-size: 10px;
          line-height: 1.2;
          letter-spacing: 0.05em;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
      }
    }

    .nav-close {
      display: none;
      width: 38px;
      height: 38px;
      border: 0;
      border-radius: 10px;
      color: var(--xyo-sidebar-text);
      background: transparent;
    }

    .nav {
      display: block;
      flex: 1;
      min-height: 0;
      overflow-x: hidden;
      overflow-y: auto;
      padding-right: 2px;
      scrollbar-width: thin;
      scrollbar-color: rgba(255, 255, 255, 0.16) transparent;

      .nav-section {
        margin: 0 0 16px;
      }

      .nav-label {
        margin: 0 0 6px;
        padding: 0 12px;
        color: var(--xyo-sidebar-muted);
        font-size: 10px;
        font-weight: 700;
        line-height: 1.8;
        letter-spacing: 0.14em;
      }

      .nav-item {
        position: relative;
        display: flex;
        align-items: center;
        min-height: 42px;
        margin: 2px 0;
        padding: 9px 11px;
        border-radius: 10px;
        color: #d8d2e1;
        background: transparent;
        font-size: 13px;
        font-weight: 540;
        line-height: 1.2;
        text-decoration: none;
        transition: color 180ms ease, background-color 180ms ease;

        > i:first-child {
          display: grid;
          flex: 0 0 28px;
          width: 28px;
          height: 28px;
          margin: 0 9px 0 0;
          place-items: center;
          border-radius: 8px;
          color: #b9b1c7;
          background: rgba(255, 255, 255, 0.045);
          font-size: 15px;
          transition: color 180ms ease, background-color 180ms ease;
        }

        span {
          min-width: 0;
          flex: 1;
        }

        .nav-arrow {
          margin: 0;
          color: #777084;
          font-size: 11px;
          opacity: 0;
          transform: translateX(-3px);
          transition: opacity 180ms ease, transform 180ms ease;
        }

        &:hover {
          color: var(--xyo-sidebar-text);
          background: rgba(255, 255, 255, 0.075);

          > i:first-child {
            color: #cfc5ff;
            background: rgba(114, 85, 233, 0.18);
          }

          .nav-arrow {
            opacity: 1;
            transform: translateX(0);
          }
        }

        &.active {
          color: #fff;
          background: var(--xyo-accent);
          font-weight: 700;

          > i:first-child {
            color: #fff;
            background: rgba(255, 255, 255, 0.18);
          }

          .nav-arrow {
            color: #fff;
            opacity: 0.7;
            transform: none;
          }
        }
      }
    }

    .promo {
      position: relative;
      flex: 0 0 auto;
      margin: 8px 0 0;
      padding: 15px;
      overflow: hidden;
      border: 1px solid rgba(255, 255, 255, 0.09);
      border-radius: 14px;
      color: #e8e3ef;
      background: var(--xyo-sidebar-raised);

      &::after {
        position: absolute;
        right: -24px;
        bottom: -30px;
        width: 92px;
        height: 92px;
        border: 22px solid rgba(114, 85, 233, 0.17);
        border-radius: 50%;
        content: "";
      }

      .promo-kicker {
        position: relative;
        z-index: 1;
        color: #ff9374;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.12em;
      }

      .title {
        position: relative;
        z-index: 1;
        margin: 7px 0 4px;
        color: var(--xyo-sidebar-text);
        font-size: 12px;
        font-weight: 650;
        line-height: 1.45;
      }

      .sub {
        position: relative;
        z-index: 1;
        margin: 0 0 11px;
        color: var(--xyo-sidebar-muted);
        font-size: 10px;
        line-height: 1.5;
      }

      .promo-btn {
        position: relative;
        z-index: 1;
        min-height: 30px;
        padding: 6px 12px;
        border: 0 !important;
        color: #fff !important;
        background: var(--xyo-primary) !important;
      }
    }
  }

  .main {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;

    .header {
      position: sticky;
      top: 0;
      z-index: 80;
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 68px;
      padding: 0 30px;
      border-bottom: 1px solid rgba(216, 209, 224, 0.86);
      background: rgba(247, 245, 250, 0.88);
      backdrop-filter: blur(18px) saturate(120%);

      .mobile-nav-toggle {
        display: none;
      }

      .header-left {
        min-width: 0;
        flex: 1;

        .route-context {
          display: flex;
          min-width: 0;
          flex-direction: column;

          span {
            color: var(--xyo-muted);
            font-size: 10px;
            font-weight: 700;
            line-height: 1.2;
            letter-spacing: 0.12em;
          }

          strong {
            margin-top: 3px;
            overflow: hidden;
            color: var(--xyo-ink-strong);
            font-size: 16px;
            font-weight: 700;
            line-height: 1.25;
            white-space: nowrap;
            text-overflow: ellipsis;
          }
        }
      }

      .header-right {
        display: flex;
        align-items: center;
        flex: 0 0 auto;
        gap: 9px;

        .header-icon,
        .wx-tag,
        .user {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 40px;
          border: 1px solid var(--xyo-line-strong);
          border-radius: 11px;
          color: var(--xyo-ink-soft);
          background: rgba(255, 255, 255, 0.76);
          box-shadow: none;
          transition: border-color 180ms ease, color 180ms ease, background-color 180ms ease;

          &:hover {
            border-color: var(--xyo-accent);
            color: var(--xyo-primary);
            background: var(--xyo-surface-raised);
          }
        }

        .header-icon {
          width: 40px;
          padding: 0;

          .bell {
            color: inherit;
            font-size: 18px;

            ::v-deep .el-badge__content {
              border: 2px solid var(--xyo-canvas);
              background: var(--xyo-danger);
            }
          }
        }

        .wx-tag {
          gap: 7px;
          padding: 0 12px;
          font-size: 12px;
          white-space: nowrap;

          i {
            margin: 0;
            color: var(--xyo-success);
            font-size: 16px;
          }

          &.off {
            color: var(--xyo-muted);

            i {
              color: var(--xyo-placeholder);
            }
          }
        }

        .user {
          gap: 8px;
          padding: 4px 10px 4px 5px;
          font-size: 12px;

          .user-avatar {
            display: grid;
            width: 30px;
            height: 30px;
            place-items: center;
            border-radius: 9px;
            color: var(--xyo-primary);
            background: var(--xyo-warm-soft);
            font-size: 12px;
            font-weight: 800;
          }

          .user-name {
            max-width: 110px;
            overflow: hidden;
            color: var(--xyo-ink);
            font-weight: 650;
            white-space: nowrap;
            text-overflow: ellipsis;
          }

          .el-icon-arrow-down {
            color: var(--xyo-muted);
            font-size: 10px;
          }
        }
      }
    }

    .page-body {
      position: relative;
      flex: 1;
      min-width: 0;
      padding: 24px 30px 42px;
      overflow: visible;
    }
  }
}

.page-loading-fade-enter-active,
.page-loading-fade-leave-active {
  transition: opacity 180ms ease;
}

.page-loading-fade-enter,
.page-loading-fade-leave-to {
  opacity: 0;
}

@media (max-width: 1180px) {
  .dash {
    .sidebar {
      flex-basis: 226px;
      width: 226px;
      padding-right: 12px;
      padding-left: 12px;
    }

    .main {
      .header {
        padding: 0 22px;
      }

      .page-body {
        padding-right: 22px;
        padding-left: 22px;
      }
    }
  }
}

@media (max-width: 768px) {
  .dash {
    display: block;
    min-height: 100svh;

    .nav-scrim {
      position: fixed;
      inset: 0;
      z-index: 1090;
      display: block;
      width: 100%;
      height: 100%;
      border: 0;
      background: rgba(23, 21, 34, 0.56);
      backdrop-filter: blur(3px);
      animation: xyo-scrim-in 180ms ease-out both;
    }

    .sidebar {
      position: fixed;
      inset: 0 auto 0 0;
      z-index: 1100;
      display: flex;
      width: min(84vw, 304px);
      height: 100svh;
      padding: 16px 14px max(18px, env(safe-area-inset-bottom));
      overflow-y: auto;
      flex-direction: column;
      border: 0;
      background: var(--xyo-sidebar);
      transform: translateX(-103%);
      transition: transform 240ms cubic-bezier(0.22, 1, 0.36, 1);

      .sidebar-head {
        margin-bottom: 14px;
      }

      .nav-close {
        display: grid;
        place-items: center;
      }

      .nav {
        display: block;
        overflow-x: hidden;
        overflow-y: auto;

        .nav-item {
          flex-direction: row;
          padding: 9px 11px;
          font-size: 13px;

          > i:first-child {
            margin: 0 9px 0 0;
            font-size: 15px;
          }
        }
      }

      .promo {
        display: block;
      }
    }

    &.nav-open .sidebar {
      transform: translateX(0);
    }

    .main {
      width: 100%;

      .header {
        height: 62px;
        padding: 0 12px;

        .mobile-nav-toggle {
          display: grid;
          flex: 0 0 40px;
          width: 40px;
          height: 40px;
          margin-right: 8px;
          padding: 0;
          place-items: center;
          border: 1px solid var(--xyo-line-strong);
          border-radius: 11px;
          color: var(--xyo-ink);
          background: rgba(255, 255, 255, 0.78);
          font-size: 18px;
        }

        .header-left {
          .route-context span {
            display: none;
          }

          .route-context strong {
            margin-top: 0;
            font-size: 14px;
          }
        }

        .header-right {
          gap: 5px;

          .header-icon,
          .wx-tag,
          .user {
            min-height: 38px;
            border-radius: 10px;
          }

          .header-icon,
          .wx-tag {
            width: 38px;
            padding: 0;
          }

          .wx-tag span,
          .user-name,
          .user .el-icon-arrow-down {
            display: none;
          }

          .user {
            padding: 3px;

            .user-avatar {
              width: 30px;
              height: 30px;
            }
          }
        }
      }

      .page-body {
        padding: 18px 12px calc(30px + env(safe-area-inset-bottom));
        overflow: visible;
      }
    }
  }
}

@keyframes xyo-scrim-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
